import React from 'react';
import CardSlot from '../parts/CardSlot';
import colors from 'theme/colors';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import styled, { keyframes } from 'styled-components';
import fonts from 'theme/fonts';
import { Calendar as CalendarIcon } from 'lucide-react';

export default function ProfileSchedule({ data, events }) {
  const hasEvent = (date) => {
    const dateString = date.toISOString().split('T')[0];
    const checkEvents = (eventList) => eventList?.some(event =>
      event?.dates?.some(eventDate => eventDate.date === dateString)
    );
    return checkEvents(events?.next) || checkEvents(events?.now);
  };

  const disableTiles = ({ date, view }) => {
    if (view === 'month') {
      const dayOfWeek = date.getUTCDay();
      const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      return data[dayMap[dayOfWeek]] === null || data[dayMap[dayOfWeek]].length === 0;
    }
    return false;
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month' && hasEvent(date)) {
      return 'react-calendar__tile--hasEvent';
    }
    return null;
  };

  return (
    <CardSlot marginTop={'0'}>
      <Header>
        <IconBox>
          <CalendarIcon size={18} color={colors.neutralLight} />
        </IconBox>
        <HeaderText>
          <Title>Agenda</Title>
          <Subtitle>Calendario de atividades</Subtitle>
        </HeaderText>
      </Header>

      <CalendarWrapper>
        <Calendar
          tileDisabled={disableTiles}
          tileClassName={tileClassName}
        />
      </CalendarWrapper>

      <Legend>
        <LegendItem>
          <LegendDot color={colors.primaryLight} />
          <LegendLabel>Evento</LegendLabel>
        </LegendItem>
        <LegendItem>
          <LegendDot color={colors.primary} filled />
          <LegendLabel>Hoje</LegendLabel>
        </LegendItem>
        <LegendItem>
          <LegendDot color={colors.nightLight} muted />
          <LegendLabel>Fechado</LegendLabel>
        </LegendItem>
      </Legend>
    </CardSlot>
  );
}

const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, ${colors.primaryLight}, ${colors.primary});
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(63, 107, 198, 0.25);
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.h3`
  font-size: ${fonts.sizes.highlight};
  font-weight: ${fonts.weight.bold};
  color: ${colors.nightDark};
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: ${fonts.sizes.small};
  color: ${colors.night};
  margin: 0;
`;

const CalendarWrapper = styled.div`
  animation: ${fadeSlideUp} 0.4s ease forwards;
  animation-delay: 0.1s;
  opacity: 0;

  .react-calendar {
    border: none;
    width: 100%;
    background: transparent;
    font-family: inherit;
  }

  .react-calendar__navigation {
    margin-bottom: 8px;
  }

  .react-calendar__navigation button {
    border-radius: 8px;
    min-width: 36px;
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};

    &:hover,
    &:focus {
      background: rgba(63, 107, 198, 0.08);
    }

    &:disabled {
      background: transparent;
      opacity: 0.4;
    }
  }

  .react-calendar__month-view__weekdays__weekday {
    color: ${colors.primary};
    font-size: ${fonts.sizes.mini};
    font-weight: ${fonts.weight.bold};
    text-transform: uppercase;

    abbr {
      text-decoration: none;
    }
  }

  .react-calendar__month-view__days__day--weekend {
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
  }

  .react-calendar__tile {
    border-radius: 8px;
    padding: 8px 4px;
    font-size: ${fonts.sizes.small};
    transition: background 0.15s ease;

    &:hover {
      background: rgba(63, 107, 198, 0.08);
    }

    &:disabled {
      background: transparent;
      opacity: 0.35;
      cursor: default;

      &:hover {
        background: transparent;
      }
    }
  }

  .react-calendar__tile--now {
    background: rgba(63, 107, 198, 0.12);
    color: ${colors.primary};
    font-weight: ${fonts.weight.bold};
    border-radius: 8px;

    &:hover {
      background: rgba(63, 107, 198, 0.18);
    }
  }

  .react-calendar__tile--hasEvent {
    background: transparent;
    color: ${colors.primary};
    font-weight: ${fonts.weight.bold};
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 4px;
      background: ${colors.primaryLight};
      width: 6px;
      height: 6px;
      border-radius: 50%;
    }

    &:hover {
      background: rgba(63, 107, 198, 0.08);
    }
  }

  .react-calendar__tile--active {
    background: ${colors.primary};
    color: ${colors.neutralLight};
    border-radius: 8px;

    &:hover {
      background: ${colors.primaryDark};
    }
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  ${({ color, filled }) => filled ? `
    background: ${color};
    box-shadow: 0 0 0 2px rgba(63, 107, 198, 0.15);
  ` : `
    background: ${color};
  `}

  ${({ muted }) => muted && `
    opacity: 0.5;
  `}
`;

const LegendLabel = styled.span`
  font-size: ${fonts.sizes.mini};
  color: ${colors.night};
`;
